import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  MessageCircle,
  X,
  Send,
  User as UserIcon,
  Sparkles,
  Zap
} from "lucide-react";

const MSAAIAssistant = ({ studentId, personalInfo, universityInfo, roomInfo, uploadedCount, residenceId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState([
    { 
      sender: "bot", 
      text: "✨ Hi! I'm MSA AI Assistant, powered by intelligent technology. I can help you with anything about the MSA Portal!\n\nAsk me about:\n• 📄 Documents\n• 🏠 Room assignments\n• 💰 Payments\n• 🔧 Maintenance\n• 📝 Leases\n\nWhat would you like to know?" 
    }
  ]);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { sender: "user", text: userMessage }]);
    setInput("");
    setIsThinking(true);

    try {
      // Call your AI backend
      const response = await axios.post("https://https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/AI/chat", {
        studentId: parseInt(studentId),
        message: userMessage
      });
      
      setMessages(prev => [...prev, { sender: "bot", text: response.data.response }]);
    } catch (error) {
      console.error("AI Error:", error);
      setMessages(prev => [...prev, { 
        sender: "bot", 
        text: "I'm having trouble connecting. Please try again in a moment." 
      }]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 250);
  };

  const quickQuestions = [
    { text: "What documents do I need?", icon: "📄" },
    { text: "What's my room number?", icon: "🏠" },
    { text: "How do I pay rent?", icon: "💰" },
    { text: "Report maintenance issue", icon: "🔧" },
    { text: "Where's my lease?", icon: "📝" },
    { text: "View announcements", icon: "📢" }
  ];

  return (
    <>
      {/* AI Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-white shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center group ring-2 ring-purple-200"
        >
          <div className="absolute inset-0 rounded-full bg-purple-500 opacity-20 animate-ping"></div>
          <div className="w-full h-full rounded-full overflow-hidden p-1.5 relative z-10">
            <img
              src="/LOGOSTAY.png"
              alt="MSA AI Assistant"
              className="w-full h-full object-contain group-hover:rotate-12 transition-transform"
            />
          </div>
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></span>
        </button>
      )}

      {/* AI Chat Window */}
      {isOpen && (
        <div className={`fixed bottom-6 right-6 z-50 w-[90vw] sm:w-96 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ${isClosing ? "opacity-0 translate-y-4 scale-95" : "opacity-100 translate-y-0 scale-100"}`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-xl overflow-hidden p-1.5 flex-shrink-0">
                  <img
                    src="/LOGOSTAY.png"
                    alt="MSA AI Assistant"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-bold">MSA AI Assistant</h3>
                    <Zap className="w-3 h-3 text-yellow-300 fill-yellow-300" />
                  </div>
                  <p className="text-purple-200 text-xs">AI-Powered • Intelligent Responses</p>
                </div>
              </div>
              <button onClick={handleClose} className="text-white/80 hover:text-white transition p-1 rounded-lg hover:bg-white/10">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[400px] max-h-[500px] bg-gradient-to-b from-gray-50 to-white">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] ${msg.sender === "user" ? "order-2" : "order-1"}`}>
                  <div className={`flex items-start gap-2 ${msg.sender === "user" ? "flex-row-reverse" : ""}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${msg.sender === "user" ? "bg-purple-600" : "bg-white ring-1 ring-purple-100 p-1"}`}>
                      {msg.sender === "user" ? (
                        <UserIcon className="w-4 h-4 text-white" />
                      ) : (
                        <img
                          src="/LOGOSTAY.png"
                          alt="MSA AI"
                          className="w-full h-full object-contain"
                        />
                      )}
                    </div>
                    <div className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${msg.sender === "user" ? "bg-purple-600 text-white rounded-br-none" : "bg-white text-gray-800 rounded-bl-none shadow-md border border-gray-100"}`}>
                      {msg.text}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {isThinking && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-none px-4 py-2.5 shadow-md border border-gray-100">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                    <span className="text-xs text-gray-500 ml-1">AI thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">✨ Quick Questions</p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {quickQuestions.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => setInput(q.text)}
                  className="flex-shrink-0 text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-full text-gray-600 hover:bg-purple-50 hover:border-purple-200 transition"
                >
                  {q.icon} {q.text}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 border-t border-gray-200 p-4 bg-white">
            <input
              type="text"
              className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 bg-white text-gray-900 placeholder-gray-400"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything about MSA Portal..."
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isThinking}
              className="p-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MSAAIAssistant;